import type { FC } from "react";

import { useHeroHeightLock } from "@ui/hooks/useHeroHeightLock";
import { useUser } from "@ui/hooks/useUser";
import classNames from "classnames";

import { HeroBackground } from "../HeroBackground/HeroBackground";
import { HeroCards } from "../HeroCards/HeroCards";
import { HeroContent } from "../HeroContent/HeroContent";
import { Promotions } from "../Promotions";
import type { HeroV2Content } from "../types/HeroV2Types";
import { buildHeroContent } from "../utils/buildHeroContent";

import "./HeroContentAuthoringWrapper.global.scss";
import styles from "./HeroContentAuthoringWrapper.module.scss";

export type HeroContentAuthoringWrapperProps = {
  data: HeroV2Content;
  isLoading?: boolean;
};

export const HeroContentAuthoringWrapper: FC<HeroContentAuthoringWrapperProps> = ({ data, isLoading }) => {
  const { isUserLoggedIn } = useUser();
  const { heroRef } = useHeroHeightLock({ enabled: !data.isContainerWidth });
  const { cards = [] } = data.content ?? {};
  const { discounts } = data?.content ?? {};
  const hasDiscounts = Array.isArray(discounts) && discounts.length > 0;

  const hasCards = cards && cards.length > 1;
  const classes = classNames("authorWrapperGlobal", styles.authorWrapper, {
    [styles.isContainerWidth]: data.isContainerWidth,
  });

  const heroCardsDependentClasses = classNames(styles.wrapper, {
    [styles.heroCardsContentWrapper]: hasCards,
    heroCardsContentWrapper: hasCards,
  });

  return (
    <div ref={heroRef} className={classes}>
      <div className={heroCardsDependentClasses}>
        <HeroBackground
          backgroundType={data.backgroundType}
          desktopImage={data.desktopImage}
          mediumImage={data.mediumImage}
          smallImage={data.smallImage}
          tabletVideoFileReference={data.tabletVideoFileReference}
          tabletVideoFileType={data.tabletVideoFileType}
          mobileVideoFileReference={data.mobileVideoFileReference}
          mobileVideoFileType={data.mobileVideoFileType}
          videoFileReference={data.videoFileReference}
          videoFileType={data.videoFileType}
          isHeroCardsShown={hasCards}
          removeVideoRoundedCorners={data.removeVideoRoundedCorners}
          renderBackgroundImage={data.renderBackgroundImage}
        />
        <div className={styles.content}>
          <HeroContent
            {...buildHeroContent(data, isUserLoggedIn, data?.isContainerWidth)}
            isLoading={isLoading}
            hasCards={hasCards}
          />

          <div className={styles.bottom}>
            {hasCards && (
              <div
                className={classNames(styles.cardsWrapper, {
                  "dark-theme": data.theme === "dark",
                  "light-theme": data.theme === "light",
                })}
                data-testid="hero-cards"
              >
                <HeroCards cards={cards} hasDiscounts={hasDiscounts} />
              </div>
            )}
            {(hasDiscounts || data.legalDisclaimer) && (
              <Promotions
                discounts={data.content.discounts}
                legalDisclaimer={data.legalDisclaimer}
                isContainerWidth={data.isContainerWidth}
                theme={data.theme || undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
